import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import color from "../../Constants/colors";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";
import { DashboardModuleKey, ModulePreference } from "../../Models/dashboard";
import {
  useMarkModuleOpened,
  useModulePreferences,
  useToggleModuleFavorite,
} from "../../Query/useDashboard";
import { CardTitle, cardSx, EmptyState } from "./DashboardCard";
import { useDashboardColors } from "./dashboardColors";
import ModuleCard from "./ModuleCard";
import { DashboardModuleDefinition, openModule } from "./dashboardModules";
import { makeTimeAgo, parseServerTime } from "./dashboardItems";

type ModuleFilter = "all" | "recent" | "favorites";

const RECENT_LIMIT = 6;

interface ModulesSectionProps {
  modules: DashboardModuleDefinition[];
}

const ModulesSection: React.FC<ModulesSectionProps> = ({ modules }) => {
  const { t, i18n } = useTranslation("home");
  const navigate = useNavigate();
  const { colors: workspace } = useWorkspaceColors();
  const { accent, accentSoft, onAccent } = useDashboardColors();
  const [filter, setFilter] = useState<ModuleFilter>("all");
  const { data: preferences = [] } = useModulePreferences();
  const { mutate: toggleFavorite } = useToggleModuleFavorite();
  const { mutate: markOpened } = useMarkModuleOpened();
  const timeAgo = makeTimeAgo(t, i18n.language);

  const byKey = new Map<DashboardModuleKey, ModulePreference>(
    preferences.map((preference) => [preference.module_key, preference]),
  );
  const accessible = modules.filter((module) => module.hasAccess);
  const favorites = accessible.filter(
    (module) => byKey.get(module.key)?.is_favorite,
  );
  const recent = accessible
    .filter((module) => byKey.get(module.key)?.last_opened_at)
    .sort(
      (a, b) =>
        (parseServerTime(byKey.get(b.key)?.last_opened_at)?.getTime() ?? 0) -
        (parseServerTime(byKey.get(a.key)?.last_opened_at)?.getTime() ?? 0),
    )
    .slice(0, RECENT_LIMIT);

  const visible =
    filter === "favorites"
      ? favorites
      : filter === "recent"
        ? recent
        : // Favorites lead the full list; everything else keeps its order.
          [
            ...modules.filter(
              (m) => m.hasAccess && byKey.get(m.key)?.is_favorite,
            ),
            ...modules.filter(
              (m) => !(m.hasAccess && byKey.get(m.key)?.is_favorite),
            ),
          ];

  const filters: { key: ModuleFilter; label: string; count?: number }[] = [
    { key: "all", label: t("hub.modules.filters.all"), count: modules.length },
    { key: "recent", label: t("hub.modules.filters.recent") },
    {
      key: "favorites",
      label: t("hub.modules.filters.favorites"),
      count: favorites.length,
    },
  ];

  const handleOpen = (module: DashboardModuleDefinition) => {
    // Internal pages are recorded by the layout's visit tracker; external
    // apps leave the hub, so they are recorded here.
    if (module.hasAccess && module.externalUrl) markOpened(module.key);
    openModule(module, navigate);
  };

  return (
    <Box component="section" sx={{ ...cardSx, p: 2.5 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
          mb: 2,
        }}
      >
        {/* Wraps the filter onto its own row when both do not fit. */}
        <Box sx={{ flex: "1 1 260px", minWidth: 0 }}>
          <CardTitle
            title={t("hub.modules.title")}
            subtitle={t("hub.modules.subtitle")}
          />
        </Box>

        <Box
          role="tablist"
          aria-label={t("hub.modules.filterLabel")}
          sx={{
            display: "inline-flex",
            p: 0.5,
            gap: 0.5,
            borderRadius: "12px",
            backgroundColor: color.gray100,
            border: `1px solid ${color.gray200}`,
            flexShrink: 0,
            maxWidth: "100%",
            overflowX: "auto",
          }}
        >
          {filters.map((option) => {
            const selected = filter === option.key;
            return (
              <Box
                key={option.key}
                component="button"
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => setFilter(option.key)}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1.5,
                  py: 0.75,
                  border: 0,
                  borderRadius: "9px",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  fontFamily: "inherit",
                  fontSize: 13,
                  fontWeight: 600,
                  color: selected ? color.white : color.gray600,
                  backgroundColor: selected
                    ? workspace.primary_dark
                    : "transparent",
                  transition: "background-color 0.15s ease",
                  "&:hover": {
                    backgroundColor: selected
                      ? workspace.primary_dark
                      : accentSoft,
                  },
                  "&:focus-visible": {
                    outline: `2px solid ${workspace.primary_dark}`,
                    outlineOffset: 1,
                  },
                }}
              >
                {option.label}
                {option.count !== undefined && (
                  <Box
                    component="span"
                    sx={{
                      px: 0.75,
                      borderRadius: "999px",
                      fontSize: 11,
                      lineHeight: "18px",
                      fontWeight: 700,
                      color: selected ? onAccent : color.gray600,
                      backgroundColor: selected ? accent : color.gray200,
                    }}
                  >
                    {option.count}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      {visible.length === 0 ? (
        <EmptyState>
          {filter === "favorites"
            ? t("hub.modules.emptyFavorites")
            : t("hub.modules.emptyRecent")}
        </EmptyState>
      ) : (
        <Box
          role="tabpanel"
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
            gap: 2,
          }}
        >
          {visible.map((module) => {
            const preference = byKey.get(module.key);
            return (
              <ModuleCard
                key={module.key}
                module={module}
                isFavorite={Boolean(preference?.is_favorite)}
                lastOpened={timeAgo(preference?.last_opened_at)}
                onOpen={() => handleOpen(module)}
                onToggleFavorite={() =>
                  toggleFavorite({
                    moduleKey: module.key,
                    isFavorite: !preference?.is_favorite,
                  })
                }
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default ModulesSection;
